import { FilterBar, FilterRow } from "@components/FilterBar";
import ObjectSelect from "@components/ObjectSelect";
import { type CommunicationStateRow, useCommunicationState } from "@features/communications/api";
import { useObjects } from "@features/devices/api";
import { PageLayoutWithHelp } from "@features/page-help";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { Alert, InputNumber, Space, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useMemo, useState } from "react";

const CommunicationDriftPage = () => {
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const { selectedObject, setSelectedObject } = useGlobalObject(objects, { required: false });
  const [threshold, setThreshold] = useState<number>(5);

  const stateQuery = useCommunicationState({
    region: selectedObject?.regionId,
    object: selectedObject?.objectId,
    limit: 500,
  });

  const data = useMemo(() => {
    if (!stateQuery.data) {
      return [];
    }
    return stateQuery.data.filter(
      (row) => typeof row.time_delta === "number" && Math.abs(row.time_delta) >= threshold,
    );
  }, [stateQuery.data, threshold]);

  const columns: ColumnsType<CommunicationStateRow> = [
    {
      title: "Счётчик",
      dataIndex: "meter_id",
      width: 140,
    },
    {
      title: "Объект",
      dataIndex: "object_name",
      width: 240,
      render: (_value, row) =>
        row.object_name ? `${row.object_name} (№${row.object_id ?? "?"})` : "—",
    },
    {
      title: "Расхождение, сек",
      dataIndex: "time_delta",
      width: 160,
      render: (_value, row) => (
        <Tag color="red">
          {row.time_delta?.toFixed(1)} (от{" "}
          {row.time_delta_at ? dayjs(row.time_delta_at).format("DD.MM HH:mm") : "—"})
        </Tag>
      ),
    },
    {
      title: "Последняя синхронизация",
      dataIndex: "sync_at",
      width: 180,
      render: (value?: string) => (value ? dayjs(value).format("DD.MM HH:mm") : "—"),
    },
    {
      title: "Связь",
      dataIndex: "connection_state",
      width: 160,
      render: (_value, row) =>
        row.connection_state === 1 ? <Tag color="green">есть</Tag> : <Tag color="red">нет</Tag>,
    },
  ];

  return (
    <PageLayoutWithHelp title="Расхождение времени счётчиков">
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <Typography.Text type="secondary">
          Отчёт показывает счётчики с расхождением времени более указанного порога. Используйте его
          для быстрой синхронизации часов.
        </Typography.Text>
        <FilterBar>
          <FilterRow>
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={selectedObject}
              onChange={(value) => setSelectedObject(value)}
              placeholder="Объект (все доступные)"
              allowClear
              style={{ minWidth: 320 }}
            />
            <Space.Compact>
              <Typography.Text
                style={{
                  padding: "4px 8px",
                  border: "1px solid #d9d9d9",
                  borderRight: 0,
                  borderRadius: 6,
                }}
              >
                Порог, сек
              </Typography.Text>
              <InputNumber
                value={threshold}
                onChange={(value) => setThreshold(value ?? 5)}
                style={{ width: 140 }}
              />
            </Space.Compact>
          </FilterRow>
        </FilterBar>

        {!stateQuery.isLoading && data.length === 0 ? (
          <Alert
            type="success"
            showIcon
            title="Критичных расхождений нет"
            description="Все обнаруженные счётчики находятся в пределах допустимого порога."
          />
        ) : (
          <EnterpriseTable<CommunicationStateRow>
            rowKey={(row) =>
              String(
                row.meter_id ??
                  row.place_id ??
                  `${row.object_id ?? "0"}-${row.feeder_id ?? "0"}-${row.channel_name ?? ""}-${
                    row.host ?? ""
                  }-${row.tcp_port ?? ""}-${row.phone ?? ""}`,
              )
            }
            columns={columns}
            dataSource={data}
            loading={stateQuery.isLoading}
            scroll={{ x: 900 }}
            size="small"
            toolbar={{ reload: true, onReload: () => stateQuery.refetch() }}
          />
        )}
      </Space>
    </PageLayoutWithHelp>
  );
};

export default CommunicationDriftPage;
